import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Award, Heart, Target, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const qualifications = [
    "Master of Arts",
    "Master of Tax",
    "Zertifizierte INQA-Coachin",
    "Betriebswirtin",
    "Vorstandsvorsitzende Lohnsteuerhilfeverein",
    "Dozentin"
  ];

  const values = [
    {
      icon: Target,
      title: "Klarheit",
      description: "Komplexe Sachverhalte verständlich aufbereiten"
    },
    {
      icon: Award,
      title: "Struktur",
      description: "Systematische Herangehensweise an jede Herausforderung"
    },
    {
      icon: Users,
      title: "Empowerment",
      description: "Menschen befähigen, unternehmerische Entscheidungen zu treffen"
    },
    {
      icon: Heart,
      title: "Persönlichkeit",
      description: "Authentische Beratung mit persönlicher Note"
    }
  ];

  return (
    <section id="about" className="py-32 bg-gradient-subtle relative overflow-hidden">
      <div className="absolute inset-0 bg-dot-pattern opacity-[0.03]"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" ref={ref}>
        {/* Banner + Chamäleon Kombi */}
        <motion.div 
          className="relative mb-16 rounded-2xl overflow-hidden shadow-[var(--shadow-large)]"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6 }}
        >
          <img
            src="/lovable-uploads/b297ec41-aecf-4260-81a5-7b5cdeb73ce7.png"
            alt="Schreibtisch-Banner in Anthrazit mit weißer Schrift"
            className="w-full h-56 md:h-72 object-cover"
            loading="lazy"
            decoding="async"
          />
          <motion.img
            src="/lovable-uploads/2d81f257-4578-4703-94e0-fc8e9b90b496.png"
            alt="Chamäleon – Symbol für Anpassungsfähigkeit"
            className="absolute -bottom-6 -left-6 w-40 md:w-56 opacity-80 mix-blend-multiply pointer-events-none select-none"
            loading="lazy"
            decoding="async"
            initial={{ x: -20, opacity: 0 }}
            animate={isInView ? { x: 0, opacity: 0.8 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
        </motion.div>

        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.h2 
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold gradient-heading mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Über mich
          </motion.h2>
          <motion.div 
            className="w-24 h-1 bg-gradient-to-r from-accent to-purple-accent mx-auto rounded-full mb-8"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          ></motion.div>
          <motion.p 
            className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Mit jahrelanger Erfahrung in Steuerwesen, Betriebswirtschaft und Bildung 
            unterstütze ich Sie dabei, Ihre unternehmerischen Ziele zu erreichen.
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-start mb-16">
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className="text-3xl font-bold text-foreground mb-8">Mein Weg</h3>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Als freiberufliche Unternehmensberaterin, Business-Coachin, Trainerin und Dozentin 
              bringe ich umfassende Expertise in den Bereichen Betriebswirtschaft, Controlling, 
              Rechnungswesen und Steuerwesen mit. Meine Mission ist es, Gründer:innen, 
              Selbständige und kleine Unternehmen mit Klarheit, Struktur und strategischem 
              Weitblick zu unterstützen.
            </p>
            
            <p className="text-muted-foreground mb-6 leading-relaxed">
              Als Mutter von drei Kindern und wohnhaft in Rosenheim verstehe ich die 
              Herausforderungen des unternehmerischen Alltags und die Balance zwischen 
              beruflichen Zielen und persönlichen Prioritäten.
            </p>

            <div className="space-y-2">
              <h4 className="font-semibold text-foreground mb-3">Qualifikationen</h4>
              <div className="flex flex-wrap gap-2">
                {qualifications.map((qual, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.3, delay: 0.5 + index * 0.05 }}
                  >
                    <Badge variant="secondary" className="text-sm">
                      {qual}
                    </Badge>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            whileHover={{ y: -5 }}
          >
            <Card className="p-10 shadow-[var(--shadow-large)] border-2 border-border/50 hover:border-accent/30 transition-all duration-300 bg-gradient-card relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-accent to-purple-accent"></div>
              <h3 className="text-3xl font-bold text-foreground mb-10 text-center">
                Meine Werte
              </h3>
              <div className="grid grid-cols-2 gap-6">
                {values.map((value, index) => (
                  <motion.div 
                    key={index} 
                    className="text-center group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <motion.div 
                      className="w-12 h-12 bg-gradient-to-br from-accent to-purple-accent rounded-xl flex items-center justify-center mx-auto mb-3 shadow-[var(--shadow-soft)]"
                      whileHover={{ rotate: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <value.icon className="w-6 h-6 text-white" strokeWidth={2} />
                    </motion.div>
                    <h4 className="font-semibold text-foreground mb-2">{value.title}</h4>
                    <p className="text-sm text-muted-foreground">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
